import { Combobox, Field, Option, makeStyles, mergeClasses, tokens, useComboboxFilter } from '@fluentui/react-components';
import { FieldValueChangedErrorType, FieldValueChangedTriggerType, TriggerItemType } from '../types';
import { useAutomationRuleTrigger } from '../use-trigger';
import { useCallback, useMemo } from 'react';
import ItemSkeleton from '../../skeleton';

export default function FieldValueChangedForm(props: {
  validations: FieldValueChangedErrorType | null;
  data: FieldValueChangedTriggerType
}) {
	const classes = useStyles();
	const {setTriggerData} = useAutomationRuleTrigger();

	const changeFieldName = useCallback((value: string | null) => {
		if (value === null) {
			setTriggerData(oldData => {
				const values = oldData as TriggerItemType;
				const fieldValueChanged = values.fieldValueChange as FieldValueChangedTriggerType;
				return {
					...oldData as TriggerItemType,
					fieldValueChange: {
						...fieldValueChanged,
						fieldNameQuery: null,
					}
				};
			});
		}

		if (value || value === '') {
			setTriggerData(oldData => {
				const values = oldData as TriggerItemType;
				const fieldValueChanged = values.fieldValueChange as FieldValueChangedTriggerType;
				return {
					...oldData as TriggerItemType,
					fieldValueChange: {
						...fieldValueChanged,
						fieldNameQuery: value
					}
				};
			});
		}
	}, []);

	const changeSelectedField = useCallback((value: string) => {
		setTriggerData(oldData => {
			const values = oldData as TriggerItemType;
			const fieldValueChanged = values.fieldValueChange as FieldValueChangedTriggerType;
			return {
				...oldData as TriggerItemType,
				fieldValueChange: {
					...fieldValueChanged,
					selectedOptions: value,
					fieldNameQuery: null,
					fieldName: value
				}
			};
		});
	}, []);

	const replaceSafeQuery = (text: string) => {
		return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	};


	const children = useComboboxFilter(
		props.data.fieldNameQuery || '',
		props.data.fieldOptions.map(item => item.value),
		{
			filter: (optionText, query) => {
				const foundLabel = props.data.fieldOptions.find(item => item.value === optionText)?.label || '';
				return new RegExp(replaceSafeQuery(query.toLowerCase())).test(foundLabel.toLowerCase());
			},
			renderOption: (optionValue) => {
				const found = props.data.fieldOptions.find(item => item.value === optionValue);
				const label = found?.label || '';
				const value = found?.value || '';

				return <Option text={label} value={value}>{label}</Option>;
			},
			noOptionsMessage: 'No option match your search.',
		});

	const fieldNameValueRender = useMemo(() => {
		if (props.data.fieldNameQuery || props.data.fieldNameQuery === '') {
			return props.data.fieldNameQuery;
		}
	
		if (props.data.fieldName && props.data.fieldNameQuery === null) {
			return props.data.fieldOptions.find(item => item.value === props.data.fieldName)?.label;
		}
	
		return '';
	}, [props.data]);

	return (
		<div className={classes.root}>
			<div className={classes.content}>
				{props.data.loading && (
					<div style={{ width: '100%' }}>
						<ItemSkeleton />
					</div>
				)}
				
				{!props.data.loading && (
					<Field
						className={classes.dropdownRoot}
						validationMessage={props?.validations?.fieldName}
					>
						<Combobox
							className={mergeClasses(classes.dropdown)}
							appearance='outline'
							placeholder='Choose a field'
							positioning='below-start'
							value={fieldNameValueRender}
							onOptionSelect={(_, data) => {
								changeSelectedField(data.optionValue as string);
							}}
							onChange={(ev) => {
								changeFieldName(ev.target.value);
							}}
						>
							<div style={{maxHeight: '320px'}}>
								{children}
							</div>
						</Combobox>
					</Field>
				)}
			</div>
		</div>
	);
}

export const useStyles = makeStyles({
	root: {
		width: '100%'
	},
	content: {
		width: '100%',
		display: 'flex',
	},
	helperTextForError: {
		width: '100%',
		color: tokens.colorPaletteRedBorder2,
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase100,
		fontWeight: tokens.fontWeightRegular,
		lineHeight: tokens.lineHeightBase100,
		padding: '0px 4px'
	},
	dropdownRoot: {
		width: '100%'
	},
	dropdown: {
		flex: '1 1 0px',
		minWidth: '120px',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		}
	}
});